"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { durations } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../primitives/Tooltip";

/**
 * Denied dim treatment. The default `muted` keeps the donor's exact
 * `opacity-50`, so existing visuals are unchanged; `subtle` is slightly less
 * dimmed for surfaces where a fully greyed control reads as broken. New tones
 * can be added here without touching any of the (53+) call sites.
 *
 * `pointer-events-none` makes the inner wrapper inert so the dimmed control
 * cannot be hovered/clicked directly; the live `relative inline-block` outer
 * div (the Tooltip trigger) is what receives hover and shows the reason.
 */
const deniedWrapperVariants = cva(
  "pointer-events-none cursor-not-allowed h-full",
  {
    variants: {
      tone: {
        muted: "opacity-50",
        subtle: "opacity-60",
      },
    },
    defaultVariants: {
      tone: "muted",
    },
  },
);

/** The dim-tone variant of the denied treatment. */
export type PermissionWrapperTone = NonNullable<
  VariantProps<typeof deniedWrapperVariants>["tone"]
>;

const DEFAULT_DENIED_MESSAGE =
  "You do not have permission in this workspace for this action";

export interface PermissionWrapperProps {
  /**
   * Whether the user has permission to perform the action. This is an
   * arbitrary boolean expression at the call site (e.g. `!isViewer && !isEditor`,
   * `!isTeamContext || hasCreatePermission`, `canRemove`).
   */
  hasPermission: boolean;
  /**
   * The single child element to wrap (typically a Button). It is cloned, so it
   * must accept a `ref`, plus `disabled` and `onClick` when permission is denied.
   */
  children: React.ReactElement;
  /**
   * Tooltip message shown when permission is denied. Carries role-specific copy
   * at the call site (e.g. "Only team owners and admins can remove Co-Creators.").
   * @default "You do not have permission in this workspace for this action"
   */
  deniedMessage?: string;
  /**
   * When true, render nothing instead of the dimmed control if permission is
   * denied (drops the control entirely).
   * @default false
   */
  hideOnDenied?: boolean;
  /**
   * Informational tooltip shown on the live control when permission IS granted
   * (e.g. "Upload documents or connect sources to your Vault."). The action
   * stays interactive.
   */
  allowedTooltip?: string;
  /**
   * Dim treatment for the denied state. Defaults to `muted` (opacity-50), the
   * donor's original look.
   * @default "muted"
   */
  tone?: PermissionWrapperTone;
}

/**
 * PermissionWrapper - gate any action and, when it is off, tell the user why.
 *
 * Wraps a single action element (usually a `Button`). When `hasPermission` is
 * false it disables the child, swallows its click, dims it, and shows the reason
 * in a Tooltip on hover/focus. When permission is granted it either renders the
 * child untouched (the hot path, zero wrapper DOM) or, if `allowedTooltip` is
 * set, wraps it in an informational Tooltip while keeping it live.
 *
 * Built on the in-house `Tooltip` primitive (Radix-backed), so focus management,
 * keyboard dismissal, portalling, collision-aware placement, and the
 * `role="tooltip"` / `aria-describedby` wiring come for free. The denied control
 * is wrapped in a live `<div>` (rather than cloning the trigger onto a disabled
 * button) because Radix `Tooltip.Trigger asChild` needs a single ref-able child,
 * and a disabled native button does not reliably forward hover/focus - the div
 * is what Radix attaches to and what receives hover. Do not collapse it onto the
 * child.
 *
 * Both Tooltip branches use `delayDuration={0}` so the reason appears instantly
 * on hover, with no wait. The denied content relies on the primitive's own
 * `z-50` tooltip layer (it portals to `document.body`, above app chrome) instead
 * of a magic z-index. When `hasPermission` flips false -> true (role change,
 * workspace switch) the child eases from dimmed to live via a short
 * opacity transition (the `durations.hover` preset), gated on
 * `useReducedMotion()` so it is instant for users who prefer reduced motion.
 *
 * Requires a `<TooltipProvider>` mounted once at the app root (unified already
 * mounts one); this component also wraps a local provider so it works in
 * isolation (e.g. in Storybook).
 *
 * SECURITY WARNING: UX/UI ONLY - NOT FOR SECURITY. Users can bypass via DOM or
 * direct API calls. ALL permission checks MUST be enforced on the backend. Never
 * rely on client-side permission checks for authorization.
 *
 * @example
 * ```tsx
 * // Disable + reason tooltip when denied
 * <PermissionWrapper hasPermission={!isViewer && !isEditor} deniedMessage="Only admins can edit this item">
 *   <Button onClick={handleEdit}>Edit</Button>
 * </PermissionWrapper>
 *
 * // Drop the control entirely when denied
 * <PermissionWrapper hasPermission={canRemove} hideOnDenied>
 *   <Button onClick={handleRemove}>Remove</Button>
 * </PermissionWrapper>
 *
 * // Informational hint on a live control
 * <PermissionWrapper hasPermission deniedMessage="..." allowedTooltip="Upload documents or connect sources to your Vault.">
 *   <Button onClick={handleUpload}>Upload</Button>
 * </PermissionWrapper>
 * ```
 */
export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  hasPermission,
  children,
  deniedMessage = DEFAULT_DENIED_MESSAGE,
  hideOnDenied = false,
  allowedTooltip,
  tone,
}) => {
  // `null` (unresolved) is treated as "allow motion" so SSR renders the
  // transition class; only an explicit `true` drops it.
  const reduced = useReducedMotion();
  const allowTransition = reduced !== true;

  // Granted: render the child as-is (zero wrapper), or wrapped in an
  // informational tooltip when `allowedTooltip` is set (action stays live).
  if (hasPermission) {
    if (allowedTooltip) {
      return (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent>{allowedTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return children;
  }

  // Denied + hide: drop the control entirely.
  if (hideOnDenied) {
    return null;
  }

  // Denied: dim the child, disable it, swallow its click, and show the reason.
  // The child is wrapped in a div (not cloned onto the trigger) so Radix has a
  // ref-able, hover-able element even though the inner control is disabled.
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="relative inline-block">
            <div
              className={cn(
                deniedWrapperVariants({ tone }),
                // Ease dimmed -> live when permission flips on. `motion-reduce:`
                // is the CSS-level fallback (covers first paint before the hook
                // resolves); the JS gate drops it entirely once `reduced` is true.
                allowTransition && "transition-opacity motion-reduce:transition-none",
              )}
              style={allowTransition ? { transitionDuration: `${durations.hover}s` } : undefined}
            >
              {React.cloneElement(
                children as React.ReactElement<{
                  disabled?: boolean;
                  onClick?: React.MouseEventHandler;
                }>,
                {
                  disabled: true,
                  onClick: (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                },
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={8}>
          {deniedMessage}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
PermissionWrapper.displayName = "PermissionWrapper";
